import { Validator } from 'fluentvalidation-ts';
import { fieldRequiredValidationMessage } from '../../../../validators/sharedValidators';
import {
  beAnInteger,
  beGreaterThan,
  beGreaterThanOrEqualTo,
  beLessThan,
  beLessThanOrEqualTo,
  beNumeric,
  haveDecimalPlaces,
} from '../../../../validators/stringValidators';
import { BuildTypeCode } from '../../../projects/project';
import {
  FloorCovering,
  floorOutputFormulaTypeEn1264,
  RoomFloorConstructionFormModel,
} from '../GetDataForQuoteOrDesignForm';
import { RoomType } from './roomTypes';

export type DesignRoomDetailsFormModel = {
  unheatedAreaInMetresSquared: string;
  voidAreaInMetresSquared: string;
  pipesPerPort: string;
  manifoldDistanceInMetres: string;
  verticalRunbackInMetres: string;
  estimatedLengthOfEachPipeInMetres: number | null;
  drawnLengthInMetres: string;
  oversupplyLengthInMetres: string;
  peripheralAreaInMetresSquared: string;
  turnPanelQuantity: string;
  straightPanelQuantity: string;
  plateAreaInMetresSquared: string;
  hasPanelsForUnheatedArea: boolean;
  temperatureDeltaInDegreesCelsius: string;
  maximumTemperatureInDegreesCelsius: string;
  screedDetails: RoomScreedDetailsFormModel | null;
};

export type RoomScreedDetailsFormModel = {
  screedConductivity: string;
  thicknessOfScreedAbovePipeInMillimetres: string;
};

export const getInitialDesignRoomDetailsFormModel = (
  buildTypeCode: BuildTypeCode,
  floorConstruction: RoomFloorConstructionFormModel | null,
  roomType: RoomType | null,
): DesignRoomDetailsFormModel => ({
  unheatedAreaInMetresSquared: '0',
  voidAreaInMetresSquared: '0',
  pipesPerPort: floorConstruction == null ? '' : getDefaultPipesPerPort(floorConstruction),
  manifoldDistanceInMetres: '',
  verticalRunbackInMetres: '2.0',
  estimatedLengthOfEachPipeInMetres: null,
  drawnLengthInMetres: '',
  oversupplyLengthInMetres: '0',
  peripheralAreaInMetresSquared: '',
  turnPanelQuantity: '',
  straightPanelQuantity: '',
  plateAreaInMetresSquared: '',
  hasPanelsForUnheatedArea: true,
  temperatureDeltaInDegreesCelsius: buildTypeCode === 'NewBuild' ? '7' : '10',
  maximumTemperatureInDegreesCelsius: getDefaultMaximumTemperatureInDegreesCelsius(roomType),
  screedDetails: floorConstruction == null ? null : getDefaultScreedDetails(floorConstruction),
});

export const getDefaultMaximumTemperatureInDegreesCelsius = (
  roomType: RoomType | null | undefined,
  floorCovering?: FloorCovering,
) => floorCovering?.temperatureLimitInDegreesCelsius?.toString() ?? (roomType === 'Bathroom' ? '32' : '29');

export const getDefaultPipesPerPort = (floorConstruction: RoomFloorConstructionFormModel): string => {
  return floorConstruction.pipeDiameterInMillimetres === 10 ? '4' : '1';
};

export const getDefaultScreedDetails = (
  floorConstruction: RoomFloorConstructionFormModel,
): RoomScreedDetailsFormModel | null => {
  if (
    floorConstruction.mainFloorOutputFormulaType === floorOutputFormulaTypeEn1264 ||
    floorConstruction.peripheralFloorOutputFormulaType === floorOutputFormulaTypeEn1264
  ) {
    const screedConductivity =
      floorConstruction.screedConductivity == null ? '' : floorConstruction.screedConductivity.toString();

    const thicknessOfScreedAbovePipeInMillimetres =
      floorConstruction.pipeDiameterInMillimetres == null
        ? ''
        : (65 - floorConstruction.pipeDiameterInMillimetres).toString();
    return {
      screedConductivity,
      thicknessOfScreedAbovePipeInMillimetres,
    };
  } else {
    return null;
  }
};

class RoomScreedDetailsFormModelValidator extends Validator<RoomScreedDetailsFormModel> {
  constructor(isDraft: boolean) {
    super();

    this.ruleFor('screedConductivity')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1));

    this.ruleFor('screedConductivity')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('thicknessOfScreedAbovePipeInMillimetres')
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0));

    this.ruleFor('thicknessOfScreedAbovePipeInMillimetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);
  }
}

const floorConstructionRequiredFieldMessage =
  'This is a required field for the chosen floor construction when the drawn length is specified';

export class DesignRoomDetailsFormModelValidator extends Validator<DesignRoomDetailsFormModel> {
  constructor(floorConstruction: RoomFloorConstructionFormModel | null, isDraft: boolean) {
    super();

    this.ruleFor('unheatedAreaInMetresSquared')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('unheatedAreaInMetresSquared')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('voidAreaInMetresSquared')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('voidAreaInMetresSquared')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('pipesPerPort')
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0));

    this.ruleFor('pipesPerPort')
      .notEmpty()
      .must(beLessThanOrEqualTo(4))
      .unless(() => isDraft);

    this.ruleFor('manifoldDistanceInMetres')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('manifoldDistanceInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('verticalRunbackInMetres')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('verticalRunbackInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('drawnLengthInMetres')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('oversupplyLengthInMetres')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .must(beLessThan(100_000));

    this.ruleFor('oversupplyLengthInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('peripheralAreaInMetresSquared')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2))
      .must(beLessThan(1_000_000));

    this.ruleFor('peripheralAreaInMetresSquared')
      .notEmpty()
      .withMessage(floorConstructionRequiredFieldMessage)
      .when(
        formModel =>
          !isDraft &&
          floorConstruction != null &&
          formModel.drawnLengthInMetres.trim() !== '' &&
          floorConstruction.requiresPeripheralArea,
      );

    this.ruleFor('turnPanelQuantity')
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0));

    this.ruleFor('turnPanelQuantity')
      .notEmpty()
      .withMessage(floorConstructionRequiredFieldMessage)
      .when(
        formModel =>
          !isDraft &&
          floorConstruction != null &&
          formModel.drawnLengthInMetres.trim() !== '' &&
          floorConstruction.requiresTurnPanelQuantity,
      );

    this.ruleFor('straightPanelQuantity')
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0));

    this.ruleFor('straightPanelQuantity')
      .notEmpty()
      .withMessage(floorConstructionRequiredFieldMessage)
      .when(
        formModel =>
          !isDraft &&
          floorConstruction != null &&
          formModel.drawnLengthInMetres.trim() !== '' &&
          floorConstruction.requiresStraightPanelQuantity,
      );

    this.ruleFor('plateAreaInMetresSquared')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2))
      .must(beLessThan(1_000_000));

    this.ruleFor('plateAreaInMetresSquared')
      .notEmpty()
      .withMessage(floorConstructionRequiredFieldMessage)
      .when(
        formModel =>
          !isDraft &&
          floorConstruction != null &&
          formModel.drawnLengthInMetres.trim() !== '' &&
          floorConstruction.requiresPlateArea,
      );

    this.ruleFor('temperatureDeltaInDegreesCelsius')
      .must(beNumeric)
      .must(beAnInteger);

    this.ruleFor('temperatureDeltaInDegreesCelsius')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beGreaterThan(0))
      .must(beLessThanOrEqualTo(16))
      .unless(() => isDraft);

    this.ruleFor('maximumTemperatureInDegreesCelsius')
      .must(beNumeric)
      .must(beAnInteger);

    this.ruleFor('maximumTemperatureInDegreesCelsius')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beLessThanOrEqualTo(32))
      .unless(() => isDraft);

    this.ruleFor('screedDetails').setValidator(() => new RoomScreedDetailsFormModelValidator(isDraft));
  }
}
